import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import styles from "./UserTable.module.scss";
import EditIcon from "@mui/icons-material/Edit";
import { config } from "../../config";
type Role = {
  RoleId: number;
  RoleName: string;
};

type User = {
  id: number;
  nameEnglish: string;
  nameArabic: string;
  contactNameEnglish: string;
  mobile: string;
  email: string;
  contactNationality: string;
  city: string;
  createdAt: string;
  statusDetails?: {
    StatusId: number;
    StatusName: string;
  };
  remarks?: string;
  [key: string]: any;
};

const UserTable: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editUser, setEditUser] = useState<any>({});
  const [loadingUser, setLoadingUser] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);


  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 50; // fixed per page
  const { BASE_URL } = config()
  const [searchTerm, setSearchTerm] = useState("");
  const filteredUsers = users.filter((u) =>
    u.nameEnglish?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const fetchUsers = async (page = 1, search = "") => {
    try {
      const res = await fetch(
        `${BASE_URL}/user?page=${page}&limit=${limit}&searchString=${search}`
      );

      const result = await res.json();

      if (res.ok && result.data?.users) {
        setUsers(result.data.users);
        setCurrentPage(result.data.currentPage);
        setTotalPages(result.data.totalPages);
        setTotalCount(result.data.totalCount); // global count
      }
    } catch (error) {
      console.error("Fetch failed", error);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchUsers(1, searchTerm);  // call API only after typing stops
    }, 500); // 500ms delay

    return () => clearTimeout(delayDebounce); // cleanup
  }, [searchTerm]);



  const fetchTotalCount = async () => {
    try {

      const res = await fetch(`${BASE_URL}/usercount`);
      const result = await res.json();

      if (res.ok && result.data?.data?.users) {
        setTotalCount(result.data.data.users.length);
      }
    } catch (error) {
      console.error("Failed to fetch total user count:", error);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, searchTerm);
    fetchRoles();
    fetchTotalCount();
  }, [currentPage]);

  const handleEdit = async (userId: number) => {
    try {
      setLoadingUser(true);

      const res = await fetch(`${BASE_URL}/getUserbyID?id=${userId}`);


      const result = await res.json();
      console.log(result)
      if (!res.ok || !result.success) {
        alert(result.message || "Failed to fetch user details");
        return;
      }

      const userData = result.data?.users;

      if (!userData) {
        alert("User not found for the given ID");
        return;
      }

      const {
        status,
        createdAt,
        statustimecount,
        statusDetails,
        roleDetails,
        id,
        ...filteredUser
      } = userData;

      // 🔥 4. Set user data into modal
      setEditUser({
        id: userId,         // always keep ID
        ...filteredUser,    // rest of user fields
      });

      setEditModal(true);
    } catch (error) {
      console.error("Error fetching user:", error);
      alert("Something went wrong while fetching user details.");
    } finally {
      setLoadingUser(false);
    }
  };


  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditUser((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files.length > 0) {
      setEditUser((prev: any) => ({ ...prev, [name]: files[0] }));
    }
  };

  const handleUpdateUser = async () => {
    try {
      const formData = new FormData();
      Object.entries(editUser).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value as any);
        }
      });

      const response = await fetch(`${BASE_URL}/updateUser`, {
        method: "PUT",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        alert("User updated successfully!");
        setEditModal(false);
        fetchUsers(currentPage);
      } else {
        alert(result.error?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Error updating user");
    }
  };


  const fetchRoles = async () => {
    try {

      const res = await fetch(`${BASE_URL}/rolelist`);
      const result = await res.json();
      if (res.ok) {
        setRoles(result.data?.roles || []);
      }
    } catch (error) {
      console.error("Failed to fetch roles", error);
    }
  };

  // useEffect(() => {
  //   fetchUsers(currentPage);
  //   fetchRoles();
  // }, [currentPage]);

  const showMessage = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 2000);
  };

  const handleView = (user: User) => {
    setSelectedUser(user);
    setSelectedRoleId(user.roleDetails?.id || user.roleId || null);
    setShowModal(true);
  };

  const handleStatusChange = async (
    StatusId: number,
    action: string,
    remarksText?: string
  ) => {
    if (!selectedUser?.id) return;

    try {

      const response = await fetch(`${BASE_URL}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedUser.id,
          StatusId,
          role: selectedRoleId,
          remarks: remarksText || "",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Status update failed:", errorData);
        return;
      }

      const data = await response.json();
      console.log(`Status updated successfully: ${action}`, data);
      setShowModal(false);
      setShowRemarksModal(false);
      showMessage(`User ${action.toLowerCase()} successfully`);
      setRemarks("");
      fetchUsers(currentPage);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleApprove = async () => {
    await handleStatusChange(1, "Approved");

    await fetch(`${BASE_URL}/sendemailuser`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: selectedUser?.email,
        name: selectedUser?.nameEnglish
      })
    });
  };


  const handleRejectClick = () => {
    if (selectedUser?.remarks) {
      setRemarks(selectedUser.remarks);
    } else {
      setRemarks("");
    }
    setShowRemarksModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!remarks.trim()) {
      alert("Please enter remarks before rejecting.");
      return;
    }

    if (!selectedUser?.id) {
      alert("User ID missing!");
      return;
    }

    try {
      const remarkRes = await fetch(`${BASE_URL}/addremarks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedUser.id, remarks }),
      });

      const remarkData = await remarkRes.json();

      if (!remarkRes.ok) {
        console.error("Failed to post remarks:", remarkData);
        alert("Failed to save remarks!");
        return;
      }

      console.log("✅ Remarks saved:", remarkData);

      await handleStatusChange(3, "Rejected", remarks);

      await fetch(`${BASE_URL}/rejectemailuser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: selectedUser.email,
          reason: remarks
        })
      });
    } catch (error) {
      console.error("❌ Error posting remarks:", error);
    }
  };

  const isFileUrl = (value: string): boolean => {
    return (
      typeof value === "string" &&
      (value.startsWith("http") || value.includes("amazonaws.com")) &&
      (value.endsWith(".jpg") ||
        value.endsWith(".jpeg") ||
        value.endsWith(".png") ||
        value.endsWith(".pdf") ||
        value.endsWith(".docx") ||
        value.endsWith(".doc"))
    );
  };

  const renderFilePreview = (fileUrl: string) => {
    if (fileUrl.endsWith(".jpg") || fileUrl.endsWith(".jpeg") || fileUrl.endsWith(".png")) {
      return (
        <div style={{ marginTop: "5px" }}>
          <img
            src={fileUrl}
            alt="Uploaded Document"
            style={{
              maxWidth: "150px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              cursor: "pointer",
            }}
            onClick={() => {
              setPreviewImage(fileUrl);
              setShowPreviewModal(true);
            }}
          />
        </div>
      );
    } else if (fileUrl.endsWith(".pdf")) {
      return (
        <div style={{ marginTop: "5px" }}>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            📄 View PDF
          </a>
        </div>
      );
    } else {
      return (
        <div style={{ marginTop: "5px" }}>
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            📎 View File
          </a>
        </div>
      );
    }
  };

  return (
    <div className={styles.container}>
      {successMessage && (
        <div className="alert alert-success text-center" role="alert">
          {successMessage}
        </div>
      )}

      {/* 🔍 Search Bar (Left aligned) */}
      {/* 🔍 Search + Total Count Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        {/* Left: Search */}
        <div>
          <input
            type="text"
            className="form-control"
            placeholder="Search by English Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "280px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              padding: "8px 10px",
              fontSize: "14px",
            }}
          />
        </div>

        {/* Right: Total Count */}
        <div className="text-end">
          <span
            style={{
              fontWeight: "bold",
              fontSize: "15px",
              background: "#f1f1f1",
              padding: "6px 12px",
              borderRadius: "8px",
              color: "#333",
              boxShadow: "0 0 4px rgba(0,0,0,0.1)",
            }}
          >
            Total Users: {totalCount}
          </span>
        </div>
      </div>



      <table className={styles.table}>
        <thead>
          <tr>
            <th>Id</th>
            <th>Name</th>
            <th>Arabic Name</th>
            <th>Mobile</th>
            <th>Email</th>
            <th>City</th>
            <th>Created At</th>
            <th>Status</th>
            <th>View</th>
            <th>Edit</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user, idx) => (
              <tr key={user.id}>
                <td>{(currentPage - 1) * limit + idx + 1}</td>

                <td>{user.nameEnglish}</td>
                <td>{user.nameArabic}</td>
                <td>{user.mobile}</td>
                <td>{user.email}</td>
                <td>{user.city}</td>
                <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}</td>

                <td>
                  <span
                    className={`${styles.status} ${user.status === "1"
                      ? styles["status-approved"]
                      : user.status === "2"
                        ? styles["status-admin"]
                        : user.status === "3"
                          ? styles["status-rejected"]
                          : user.status === "4"
                            ? styles["status-yet-to-approve"]
                            : styles["status-unknown"]
                      }`}
                  >
                    {user.status === "1"
                      ? "Approved"
                      : user.status === "2"
                        ? "Admin"
                        : user.status === "3"
                          ? "Rejected"
                          : user.status === "4"
                            ? "Yet to approve"
                            : "Unknown"}
                  </span>
                </td>
                <td>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => handleEdit(user.id)}

                  >
                    <EditIcon />
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-link"
                    onClick={() => handleView(user)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={9} className="text-center text-muted">
                No users found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <Button
          variant="outline-primary"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        >
          ← Previous
        </Button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <Button
          variant="outline-primary"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        >
          Next →
        </Button>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser ? (
            <div className={styles.modalContent}>
              {Object.entries(selectedUser).map(([key, value]) => {
                if (key === "role" || key === "status") return null;
                if (!value) return null;
                return (
                  <div key={key} style={{ marginBottom: "10px" }}>
                    <strong>{key}:</strong>{" "}
                    {typeof value === "string" && isFileUrl(value)
                      ? renderFilePreview(value)
                      : typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                  </div>
                );
              })}
              {selectedUser.statusDetails?.StatusId !== 1 && selectedUser.remarks && (
                <div style={{ marginTop: "10px" }}>
                  <strong>Remarks:</strong> {selectedUser.remarks}
                </div>
              )}
            </div>
          ) : (
            <p>Loading...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleApprove}>
            Approve
          </Button>
          <Button variant="danger" onClick={handleRejectClick}>
            Reject
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRemarksModal} onHide={() => setShowRemarksModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject Remarks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="remarksTextarea">
              <Form.Label>Please enter your remarks for rejection:</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Enter rejection reason..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemarksModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRejectSubmit}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} centered size="lg">
        <Modal.Body className="text-center">
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "10px",
                boxShadow: "0 0 10px rgba(0,0,0,0.3)",
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={editModal}
        onHide={() => setEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit User Details (ID: {editUser?.id || selectedUser?.id})</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingUser ? (
            <p>Loading user details...</p>
          ) : Object.keys(editUser || {}).length > 0 ? (
            <Form>
              <div className="container-fluid">
                <div className="row g-4">
                  {Object.entries(editUser).map(([key, value]) => {
                    if (["id", "createdAt", "updatedAt", "statustimecount", "statusDetails", "roleDetails", "brokerAgentId", "lastLoginDates", "emiratesIssueDate", "emiratesExpiryDate", "RERA_Expiry_Date", "passportExpiryDate", "passportIssueDate", "emiratesExpiryDate", "confirmPassword", "role", "remarks", "EnglishTranslatedDocs"].includes(key))
                      return null;

                    const label =
                      key.charAt(0).toUpperCase() +
                      key
                        .slice(1)
                        .replace(/([A-Z])/g, " $1");

                    if (typeof value === "string" && value.startsWith("http")) {
                      return (
                        <div key={key} className="col-md-4 col-sm-6 col-12 mb-3">
                          <Form.Group controlId={key}>
                            <Form.Label className="fw-bold text-secondary">{label}</Form.Label>
                            <div className="mb-2">
                              <a href={value} target="_blank" rel="noreferrer" className="text-primary text-decoration-none">
                                📎 View Existing File
                              </a>
                            </div>
                            <Form.Control type="file" name={key} onChange={handleFileChange} />
                          </Form.Group>
                        </div>
                      );
                    }

                    return (
                      <div key={key} className="col-md-4 col-sm-6 col-12 mb-3">
                        <Form.Group controlId={key}>
                          <Form.Label className="fw-bold text-secondary">{label}</Form.Label>
                          <Form.Control
                            type={
                              key.toLowerCase().includes("date")
                                ? "date"
                                : key.toLowerCase().includes("email")
                                  ? "email"
                                  : "text"
                            }
                            name={key}
                            value={typeof value === "object" ? JSON.stringify(value) : String(value)}
                            onChange={handleEditChange}
                            className="rounded-2 border border-secondary-subtle"
                          />
                        </Form.Group>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Form>
          ) : (
            <p>No editable fields available.</p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateUser}>
            Update User
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserTable;
